import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { toast } from 'sonner'
import { createBrowserLabelPrinter } from '@/lib/label/browser'
import { createDymoLabelPrinter } from '@/lib/label/dymo'
import type {
  LabelPrinterDevice,
  LabelPrinterKind,
  LabelPrinterStrategy,
  LabelPrintOptions,
  PrintEntryLabelPayload,
} from '@/lib/label/types'
import { LabelPrinterError } from '@/lib/label/types'

const STRATEGY_STORAGE_KEY = 'honeyshow/label-printer/strategy'
const DEVICE_STORAGE_KEY = 'honeyshow/label-printer/device'
const DYMO_MAX_ATTEMPTS = 4
const RETRY_BASE_DELAY_MS = 1500
const RETRY_MAX_DELAY_MS = 15000
const JOB_HISTORY_LIMIT = 30
const TEST_PRINT_JOB_KEY = 'label-printer/test'

type LabelPrinterJobStatus = 'pending' | 'success' | 'error'

export type LabelPrinterJob = {
  id: string
  key: string | null
  status: LabelPrinterJobStatus
  attempts: number
  errorMessage: string | null
  errorHelpUrl: string | null
  errorHelpLabel: string | null
  strategy: LabelPrinterKind
  createdAt: number
  lastAttemptAt: number | null
  nextRetryAt: number | null
}

type InternalPrintJob = {
  id: string
  key: string | null
  payload: PrintEntryLabelPayload
  copies: number
  printer: LabelPrinterDevice | null
  strategy: LabelPrinterStrategy
  strategyKind: LabelPrinterKind
  createdAt: number
  status: LabelPrinterJobStatus
  attempts: number
  error: Error | null
  lastAttemptAt: number | null
  nextRetryAt: number | null
  maxAttempts: number
  resolve: () => void
  reject: (error: Error) => void
}

function readEnvStrategy(): LabelPrinterKind {
  const raw = import.meta.env.VITE_LABEL_PRINTER
  if (raw === 'dymo') return 'dymo'
  return 'browser'
}

function readEnvServiceUrl(): string | undefined {
  const raw = import.meta.env.VITE_DYMO_SERVICE_URL
  if (!raw || raw.trim().length === 0) return undefined
  return raw.trim()
}

function createStrategy(kind: LabelPrinterKind): LabelPrinterStrategy {
  if (kind === 'dymo') {
    return createDymoLabelPrinter({ serviceUrl: readEnvServiceUrl() })
  }
  return createBrowserLabelPrinter()
}

type LabelPrinterContextValue = {
  strategy: LabelPrinterKind
  strategyLabel: string
  printers: LabelPrinterDevice[]
  selectedPrinter: LabelPrinterDevice | null
  loadingPrinters: boolean
  error: Error | null
  refreshPrinters: () => Promise<void>
  selectPrinter: (deviceId: string) => void
  setStrategy: (strategy: LabelPrinterKind) => void
  printEntryLabel: (
    payload: PrintEntryLabelPayload,
    overrides?: Pick<LabelPrintOptions, 'copies' | 'printer' | 'jobKey'>
  ) => Promise<void>
  testPrint: () => Promise<void>
  jobs: LabelPrinterJob[]
  getJobStatus: (key: string) => LabelPrinterJob | undefined
  discoveryActive: boolean
}

const LabelPrinterContext = createContext<LabelPrinterContextValue | undefined>(undefined)

type LabelPrinterDiscoveryController = {
  discoveryActive: boolean
  setScopeState: (scopeId: string, active: boolean) => void
}

const LabelPrinterDiscoveryContext = createContext<LabelPrinterDiscoveryController | null>(null)

function loadStoredStrategy(): LabelPrinterKind | null {
  try {
    const stored = window.localStorage.getItem(STRATEGY_STORAGE_KEY)
    if (stored === 'browser' || stored === 'dymo') {
      return stored
    }
    return null
  } catch {
    return null
  }
}

function storeStrategy(kind: LabelPrinterKind) {
  try {
    window.localStorage.setItem(STRATEGY_STORAGE_KEY, kind)
  } catch {
    // ignore
  }
}

function loadStoredDevice(strategy: LabelPrinterKind): string | null {
  try {
    const stored = window.localStorage.getItem(`${DEVICE_STORAGE_KEY}:${strategy}`)
    return stored || null
  } catch {
    return null
  }
}

function storeDevice(strategy: LabelPrinterKind, deviceId: string) {
  try {
    window.localStorage.setItem(`${DEVICE_STORAGE_KEY}:${strategy}`, deviceId)
  } catch {
    // ignore
  }
}

function generateJobId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `job-${Math.random().toString(36).slice(2, 10)}`
}

export function LabelPrinterProvider({ children }: { children: ReactNode }) {
  const defaultStrategy = useMemo(() => readEnvStrategy(), [])
  const [strategyKind, setStrategyKind] = useState<LabelPrinterKind>(() => {
    if (typeof window === 'undefined') return defaultStrategy
    return loadStoredStrategy() ?? defaultStrategy
  })

  const strategy = useMemo(() => createStrategy(strategyKind), [strategyKind])
  const label = useMemo(() => strategy.label, [strategy])
  const [printers, setPrinters] = useState<LabelPrinterDevice[]>([])
  const [selectedPrinterId, setSelectedPrinterId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return loadStoredDevice(strategyKind)
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const discoveryScopesRef = useRef<Map<string, true>>(new Map())
  const [discoveryActive, setDiscoveryActive] = useState(false)
  const discoveryActiveRef = useRef(discoveryActive)

  const [jobs, setJobs] = useState<LabelPrinterJob[]>([])
  const jobsMapRef = useRef<Map<string, InternalPrintJob>>(new Map())
  const jobsByKeyRef = useRef<Map<string, string>>(new Map())
  const queueRef = useRef<InternalPrintJob[]>([])
  const activeJobRef = useRef<InternalPrintJob | null>(null)
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const processQueueRef = useRef<() => void>(() => undefined)

  const toPublicJob = useCallback((job: InternalPrintJob): LabelPrinterJob => {
    const labelError = job.error instanceof LabelPrinterError ? job.error : null
    return {
      id: job.id,
      key: job.key,
      status: job.status,
      attempts: job.attempts,
      errorMessage: job.error?.message ?? null,
      errorHelpUrl: labelError?.helpUrl ?? null,
      errorHelpLabel: labelError?.helpLabel ?? null,
      strategy: job.strategyKind,
      createdAt: job.createdAt,
      lastAttemptAt: job.lastAttemptAt,
      nextRetryAt: job.nextRetryAt,
    }
  }, [])

  const syncJobsSnapshot = useCallback(() => {
    const entries = Array.from(jobsMapRef.current.values()).sort(
      (a, b) => b.createdAt - a.createdAt
    )
    const trimmed = entries.slice(0, JOB_HISTORY_LIMIT)
    const keepIds = new Set(trimmed.map(item => item.id))
    for (const id of Array.from(jobsMapRef.current.keys())) {
      if (!keepIds.has(id)) {
        jobsMapRef.current.delete(id)
      }
    }
    const latestByKey = new Map<string, string>()
    for (const job of trimmed) {
      if (job.key && !latestByKey.has(job.key)) {
        latestByKey.set(job.key, job.id)
      }
    }
    jobsByKeyRef.current = latestByKey
    setJobs(trimmed.map(toPublicJob))
  }, [toPublicJob])

  useEffect(() => {
    discoveryActiveRef.current = discoveryActive
  }, [discoveryActive])

  useEffect(() => {
    return () => {
      for (const timeoutId of timersRef.current.values()) {
        clearTimeout(timeoutId)
      }
      timersRef.current.clear()
      queueRef.current = []
      activeJobRef.current = null
    }
  }, [])

  const setDiscoveryScopeState = useCallback((scopeId: string, active: boolean) => {
    if (!scopeId) return
    const scopes = discoveryScopesRef.current
    const hasScope = scopes.has(scopeId)
    if (active) {
      if (!hasScope) {
        scopes.set(scopeId, true)
        setDiscoveryActive(scopes.size > 0)
      }
      return
    }
    if (hasScope) {
      scopes.delete(scopeId)
      setDiscoveryActive(scopes.size > 0)
    }
  }, [])

  const refreshPrinters = useCallback(async () => {
    const allowsDiscovery = strategyKind !== 'dymo' || discoveryActiveRef.current
    if (!allowsDiscovery) {
      setLoading(false)
      setPrinters([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const list = await strategy.listPrinters()
      if (strategyKind === 'dymo' && !discoveryActiveRef.current) {
        setLoading(false)
        setPrinters([])
        setError(null)
        return
      }
      setPrinters(list)
      if (list.length > 0) {
        const fallbackId = list[0].id
        setSelectedPrinterId(current => {
          if (!current) {
            storeDevice(strategyKind, fallbackId)
            return fallbackId
          }
          const stillExists = list.some(printer => printer.id === current)
          if (stillExists) return current
          storeDevice(strategyKind, fallbackId)
          return fallbackId
        })
      } else {
        setSelectedPrinterId(null)
      }
    } catch (err) {
      const actual = err instanceof Error ? err : new Error('Unknown printer error')
      setError(actual)
      setPrinters([])
    } finally {
      setLoading(false)
    }
  }, [strategy, strategyKind])

  useEffect(() => {
    setSelectedPrinterId(loadStoredDevice(strategyKind))
    setPrinters([])
    setError(null)
    setLoading(true)
    void refreshPrinters()
  }, [strategyKind, refreshPrinters])

  useEffect(() => {
    if (strategyKind !== 'dymo') return
    if (!discoveryActive) {
      setPrinters([])
      setLoading(false)
      setError(null)
      return
    }
    void refreshPrinters()
  }, [discoveryActive, refreshPrinters, strategyKind])

  const selectPrinter = useCallback(
    (deviceId: string) => {
      setSelectedPrinterId(deviceId)
      storeDevice(strategyKind, deviceId)
    },
    [strategyKind]
  )

  const setStrategy = useCallback((next: LabelPrinterKind) => {
    setStrategyKind(next)
    storeStrategy(next)
  }, [])

  const selectedPrinter = useMemo(() => {
    if (!selectedPrinterId) return null
    return printers.find(printer => printer.id === selectedPrinterId) ?? null
  }, [printers, selectedPrinterId])

  const runJob = useCallback(
    async (job: InternalPrintJob) => {
      const existingTimer = timersRef.current.get(job.id)
      if (existingTimer !== undefined) {
        clearTimeout(existingTimer)
        timersRef.current.delete(job.id)
      }

      job.attempts += 1
      job.status = 'pending'
      job.error = null
      job.lastAttemptAt = Date.now()
      job.nextRetryAt = null
      syncJobsSnapshot()

      try {
        await job.strategy.printEntryLabel(job.payload, {
          printer: job.printer ?? undefined,
          copies: job.copies,
        })
        job.status = 'success'
        job.error = null
        job.nextRetryAt = null
        syncJobsSnapshot()
        job.resolve()
      } catch (error) {
        const actual = error instanceof Error ? error : new Error('Unknown label printer error')
        job.error = actual
        if (job.strategyKind === 'dymo' && job.attempts < job.maxAttempts) {
          const delay = Math.min(RETRY_MAX_DELAY_MS, RETRY_BASE_DELAY_MS * 2 ** (job.attempts - 1))
          job.nextRetryAt = job.lastAttemptAt + delay
          job.status = 'pending'
          syncJobsSnapshot()
          const timeoutId = setTimeout(() => {
            timersRef.current.delete(job.id)
            queueRef.current.push(job)
            processQueueRef.current()
          }, delay)
          timersRef.current.set(job.id, timeoutId)
          return
        }

        job.status = 'error'
        job.nextRetryAt = null
        syncJobsSnapshot()
        job.reject(actual)
      } finally {
        if (activeJobRef.current?.id === job.id) {
          activeJobRef.current = null
        }
        processQueueRef.current()
      }
    },
    [syncJobsSnapshot]
  )

  const processQueue = useCallback(() => {
    if (activeJobRef.current) return
    const next = queueRef.current.shift()
    if (!next) return
    activeJobRef.current = next
    void runJob(next)
  }, [runJob])

  useEffect(() => {
    processQueueRef.current = processQueue
  }, [processQueue])

  const printEntryLabel = useCallback(
    (
      payload: PrintEntryLabelPayload,
      overrides?: Pick<LabelPrintOptions, 'copies' | 'printer' | 'jobKey'>
    ) => {
      const jobKey = overrides?.jobKey ?? null
      const copiesRaw = overrides?.copies ?? 1
      const copies = Number.isFinite(copiesRaw) ? Math.max(1, Math.floor(copiesRaw)) : 1
      const printer = overrides?.printer ?? selectedPrinter ?? null

      if (strategyKind === 'dymo' && !printer) {
        return Promise.reject(new LabelPrinterError('Select a DYMO printer before printing.'))
      }

      return new Promise<void>((resolve, reject) => {
        const jobId = generateJobId()
        const job: InternalPrintJob = {
          id: jobId,
          key: jobKey,
          payload,
          copies,
          printer,
          strategy,
          strategyKind,
          createdAt: Date.now(),
          status: 'pending',
          attempts: 0,
          error: null,
          lastAttemptAt: null,
          nextRetryAt: null,
          maxAttempts: strategyKind === 'dymo' ? DYMO_MAX_ATTEMPTS : 1,
          resolve,
          reject,
        }

        jobsMapRef.current.set(jobId, job)
        queueRef.current.push(job)
        syncJobsSnapshot()
        processQueue()
      })
    },
    [selectedPrinter, strategy, strategyKind, syncJobsSnapshot, processQueue]
  )

  const getJobStatus = useCallback(
    (key: string) => {
      const jobId = jobsByKeyRef.current.get(key)
      if (!jobId) return undefined
      const job = jobsMapRef.current.get(jobId)
      if (!job) return undefined
      return toPublicJob(job)
    },
    [toPublicJob]
  )

  const testPrint = useCallback(() => {
    const payload: PrintEntryLabelPayload = {
      entryCode: 'TEST',
      classCode: 'LABEL',
      showName: 'Test Print',
    }
    return printEntryLabel(payload, { copies: 1, jobKey: TEST_PRINT_JOB_KEY })
  }, [printEntryLabel])

  const value = useMemo<LabelPrinterContextValue>(
    () => ({
      strategy: strategyKind,
      strategyLabel: label,
      printers,
      selectedPrinter,
      loadingPrinters: loading,
      error,
      refreshPrinters,
      selectPrinter,
      setStrategy,
      printEntryLabel,
      testPrint,
      jobs,
      getJobStatus,
      discoveryActive,
    }),
    [
      strategyKind,
      label,
      printers,
      selectedPrinter,
      loading,
      error,
      refreshPrinters,
      selectPrinter,
      setStrategy,
      printEntryLabel,
      testPrint,
      jobs,
      getJobStatus,
      discoveryActive,
    ]
  )

  const discoveryController = useMemo<LabelPrinterDiscoveryController>(
    () => ({
      discoveryActive,
      setScopeState: setDiscoveryScopeState,
    }),
    [discoveryActive, setDiscoveryScopeState]
  )

  return (
    <LabelPrinterContext.Provider value={value}>
      <LabelPrinterDiscoveryContext.Provider value={discoveryController}>
        {children}
      </LabelPrinterDiscoveryContext.Provider>
    </LabelPrinterContext.Provider>
  )
}

export function useLabelPrinter(): LabelPrinterContextValue {
  const context = useContext(LabelPrinterContext)
  if (!context) {
    throw new Error('useLabelPrinter must be used within a LabelPrinterProvider')
  }
  return context
}

export function usePrintEntryLabelWithFeedback(props: {
  onSuccess?: () => void
  onError?: (error: Error) => void
}) {
  const { onSuccess, onError } = props
  const { printEntryLabel } = useLabelPrinter()
  const [isPrinting, setIsPrinting] = useState(false)

  const run = useCallback(
    async (payload: PrintEntryLabelPayload, overrides?: Pick<LabelPrintOptions, 'copies'>) => {
      setIsPrinting(true)
      try {
        await printEntryLabel(payload, overrides)
        onSuccess?.()
      } catch (error) {
        const actual = error instanceof Error ? error : new Error('Failed to print label')
        toast.error(actual.message)
        onError?.(actual)
        throw actual
      } finally {
        setIsPrinting(false)
      }
    },
    [printEntryLabel, onSuccess, onError]
  )

  return { printEntryLabel: run, isPrinting }
}

export function useLabelPrinterDiscovery(active: boolean): boolean {
  const controller = useContext(LabelPrinterDiscoveryContext)
  if (!controller) {
    throw new Error('useLabelPrinterDiscovery must be used within a LabelPrinterProvider')
  }
  const scopeId = useId()

  useEffect(() => {
    controller.setScopeState(scopeId, active)
    return () => {
      controller.setScopeState(scopeId, false)
    }
  }, [active, controller, scopeId])

  return controller.discoveryActive
}
